import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Avatar,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Tooltip,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  PrintOutlined as PrintClassIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import { API_URL } from '../utils/api';
import { sortStudentsByGradeClassroom } from '../utils/classroomSort';

const Students = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [dbClasses, setDbClasses] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [gradeFilter, setGradeFilter] = useState('all');
  const [classroomFilter, setClassroomFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, student: null });
  const [printClassDialog, setPrintClassDialog] = useState(false);
  const [printClassGrade, setPrintClassGrade] = useState('');
  const [printClassRombel, setPrintClassRombel] = useState('');
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({
    studentId: '',
    name: '',
    grade: '',
    classroom: '',
    phone: '',
    parentPhone: '',
    gender: '',
    address: '',
  });

  const grades = [1, 2, 3, 4, 5, 6];
  const classrooms = ['A', 'B', 'C']; // SD: 3 rombel per kelas (1A, 1B, 1C, 2A, 2B, 2C, ...)
  const { colors, mode } = useThemeContext();
  const cardStyle = {
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.card,
    boxShadow: 'none',
  };

  useEffect(() => {
    loadStudents();
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/classes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Gagal memuat data kelas');
      }

      const result = await response.json();
      if (result.success) {
        setDbClasses(result.data);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const loadStudents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Token autentikasi tidak ditemukan', 'error');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/employees?person_type=student`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Gagal memuat data siswa');
      }

      const result = await response.json();
      if (result.success) {
        setStudents(result.data);
      } else {
        showSnackbar(result.message || 'Gagal memuat data siswa', 'error');
      }
    } catch (error) {
      console.error('Error loading students:', error);
      showSnackbar('Gagal memuat data siswa', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (student = null) => {
    if (student) {
      setEditingStudent(student);
      setFormData({
        studentId: student.student_id || '',
        name: student.full_name || '',
        grade: student.grade ?? '',
        classroom: student.classroom ?? '',
        phone: student.phone || '',
        parentPhone: student.parent_phone || '',
        gender: student.gender || '',
        address: student.address || '',
      });
    } else {
      setEditingStudent(null);
      setFormData({
        studentId: '',
        name: '',
        grade: '',
        classroom: '',
        phone: '',
        parentPhone: '',
        gender: '',
        address: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStudent(null);
    setFormData({
      studentId: '',
      name: '',
      grade: '',
      classroom: '',
      phone: '',
      parentPhone: '',
      gender: '',
      address: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Token autentikasi tidak ditemukan', 'error');
        return;
      }

      const studentData = {
        student_id: (formData.studentId || '').trim(),
        full_name: (formData.name || '').trim(),
        hire_date: new Date().toISOString().split('T')[0],
        person_type: 'student',
      };

      // Only send optional fields when they are provided (avoid '' failing backend validators)
      if (formData.grade !== '' && formData.grade !== null && formData.grade !== undefined) {
        studentData.grade = Number(formData.grade);
      }
      if ((formData.classroom || '').trim()) {
        studentData.classroom = String(formData.classroom).trim();
      }
      if ((formData.phone || '').trim()) {
        studentData.phone = String(formData.phone).trim();
      }
      if ((formData.parentPhone || '').trim()) {
        studentData.parent_phone = String(formData.parentPhone).trim();
      }
      if ((formData.gender || '').trim()) {
        studentData.gender = String(formData.gender).trim();
      }
      if ((formData.address || '').trim()) {
        studentData.address = String(formData.address).trim();
      }

      if (editingStudent) {
        // Update student
        const response = await fetch(`${API_URL}/api/employees/${editingStudent.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(studentData)
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          const details = Array.isArray(err.details) ? err.details.map(d => d.msg).filter(Boolean).join(', ') : '';
          throw new Error(details ? `${err.error || err.message || 'Validasi input gagal'}: ${details}` : (err.error || err.message || 'Gagal mengupdate siswa'));
        }

        const result = await response.json();
        if (result.success) {
          showSnackbar('Siswa berhasil diperbarui', 'success');
          loadStudents(); // Reload data
        } else {
          showSnackbar(result.error || result.message || 'Gagal mengupdate siswa', 'error');
        }
      } else {
        // Add new student
        const response = await fetch(`${API_URL}/api/employees`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(studentData)
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          const details = Array.isArray(err.details) ? err.details.map(d => d.msg).filter(Boolean).join(', ') : '';
          throw new Error(details ? `${err.error || err.message || 'Validasi input gagal'}: ${details}` : (err.error || err.message || 'Gagal menambahkan siswa'));
        }

        const result = await response.json();
        if (result.success) {
          showSnackbar('Siswa berhasil ditambahkan', 'success');
          loadStudents(); // Reload data
        } else {
          showSnackbar(result.error || result.message || 'Gagal menambahkan siswa', 'error');
        }
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving student:', error);
      showSnackbar(error.message || 'Gagal menyimpan data siswa', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (student) => {
    setDeleteConfirm({ open: true, student });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, student: null });
  };

  const handleDeleteConfirm = async () => {
    const student = deleteConfirm.student;
    if (!student) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Token autentikasi tidak ditemukan', 'error');
        return;
      }

      const response = await fetch(`${API_URL}/api/employees/${student.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Gagal menghapus siswa');
      }

      const result = await response.json();
      if (result.success) {
        showSnackbar('Siswa berhasil dihapus', 'success');
        loadStudents(); // Reload data
      } else {
        showSnackbar(result.error || result.message || 'Gagal menghapus siswa', 'error');
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      showSnackbar(error.message || 'Gagal menghapus siswa', 'error');
    } finally {
      setDeleteConfirm({ open: false, student: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredStudents = sortStudentsByGradeClassroom(
    students.filter((student) => {
      const matchesSearch =
        student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.student_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.grade && student.grade.toString().includes(searchTerm)) ||
        (student.classroom && student.classroom.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesGrade =
        gradeFilter === 'all' || (student.grade && student.grade.toString() === gradeFilter.toString());

      const matchesClassroom =
        classroomFilter === 'all' || (student.classroom && student.classroom === classroomFilter);

      return matchesSearch && matchesGrade && matchesClassroom;
    })
  );

  const openPrintClassDialog = () => {
    setPrintClassGrade(gradeFilter !== 'all' ? String(gradeFilter) : '');
    setPrintClassRombel(classroomFilter !== 'all' ? String(classroomFilter) : '');
    setPrintClassDialog(true);
  };

  const handlePrintClassCards = () => {
    if (!printClassGrade) {
      showSnackbar('Pilih kelas (angka) terlebih dahulu', 'warning');
      return;
    }
    const qs = new URLSearchParams({ grade: printClassGrade });
    if (printClassRombel) {
      qs.set('classroom', printClassRombel);
    }
    setPrintClassDialog(false);
    navigate(`/students/print-class?${qs}`);
  };

  const handleQuickPrintClassFromFilter = () => {
    if (gradeFilter === 'all') {
      openPrintClassDialog();
      return;
    }
    const qs = new URLSearchParams({ grade: gradeFilter });
    if (classroomFilter !== 'all') {
      qs.set('classroom', classroomFilter);
    }
    navigate(`/students/print-class?${qs}`);
  };

  const getGenderColor = (gender) => {
    return gender === 'male' ? 'primary' : 'secondary';
  };

  const getGenderText = (gender) => {
    return gender === 'male' ? 'Laki-laki' : 'Perempuan';
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, gradeFilter, classroomFilter]);

  return (
    <Box sx={{ color: colors.textPrimary }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Data Siswa
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Tambah Siswa
        </Button>
      </Box>

      {/* Search and Filter */}
      <Card sx={{ ...cardStyle, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Cari nama, NIS, kelas, atau rombel..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Kelas</InputLabel>
                  <Select
                    value={gradeFilter}
                    label="Kelas"
                    onChange={(e) => setGradeFilter(e.target.value)}
                  >
                    <MenuItem value="all">Semua</MenuItem>
                    {/* Unique grades from students or dbClasses */}
                    {Array.from(new Set([
                      ...grades,
                      ...dbClasses.map(c => {
                        const m = c.name.match(/\d+/);
                        return m ? parseInt(m[0]) : null;
                      }).filter(g => g !== null)
                    ])).sort((a, b) => a - b).map((grade) => (
                      <MenuItem key={grade} value={grade.toString()}>
                        Kelas {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Rombel</InputLabel>
                  <Select
                    value={classroomFilter}
                    label="Rombel"
                    onChange={(e) => setClassroomFilter(e.target.value)}
                  >
                    <MenuItem value="all">Semua</MenuItem>
                    {/* Unique classrooms from students or dbClasses */}
                    {Array.from(new Set([
                      ...classrooms,
                      ...dbClasses.map(c => {
                        const m = c.name.match(/[A-Z]$/i);
                        return m ? m[0].toUpperCase() : null;
                      }).filter(c => c !== null)
                    ])).sort().map((cls) => (
                      <MenuItem key={cls} value={cls}>
                        Rombel {cls}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintClassIcon />}
                  onClick={handleQuickPrintClassFromFilter}
                >
                  Cetak Kartu Kelas
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadStudents}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Students Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: mode === 'dark' ? '#1f2937' : 'grey.50' }}>
              <TableCell>NIS</TableCell>
              <TableCell>Nama</TableCell>
              <TableCell>Kelas</TableCell>
              <TableCell>Rombel</TableCell>
              <TableCell>Telp Ortu</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredStudents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  Tidak ada data siswa
                </TableCell>
              </TableRow>
            ) : (
              filteredStudents
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>{student.student_id}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{student.full_name}</TableCell>
                    <TableCell>{student.grade}</TableCell>
                    <TableCell>{student.classroom}</TableCell>
                    <TableCell>{student.parent_phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={getGenderText(student.gender)}
                        size="small"
                        color={getGenderColor(student.gender)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Cetak Kartu QR">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/students/${student.id}/card`)}
                            sx={{ color: 'primary.main' }}
                          >
                            <PrintIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(student)}
                            sx={{ color: 'info.main' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Hapus">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(student)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredStudents.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Baris per halaman"
        />
      </TableContainer>

      {/* Cetak kartu per kelas */}
      <Dialog
        open={printClassDialog}
        onClose={() => setPrintClassDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cetak Kartu Per Kelas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Semua siswa di kelas terpilih akan dibuka dalam satu halaman cetak (2 kolom per lembar A4).
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Kelas</InputLabel>
            <Select
              value={printClassGrade}
              label="Kelas"
              onChange={(e) => setPrintClassGrade(e.target.value)}
            >
              {grades.map((g) => (
                <MenuItem key={g} value={String(g)}>
                  Kelas {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Rombel</InputLabel>
            <Select
              value={printClassRombel}
              label="Rombel"
              onChange={(e) => setPrintClassRombel(e.target.value)}
            >
              <MenuItem value="">Semua rombel di kelas ini</MenuItem>
              {classrooms.map((c) => (
                <MenuItem key={c} value={c}>
                  Rombel {c} (mis. {printClassGrade || '?'}{c})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintClassDialog(false)}>Batal</Button>
          <Button variant="contained" startIcon={<PrintClassIcon />} onClick={handlePrintClassCards}>
            Buka Halaman Cetak
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Konfirmasi Hapus</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Apakah Anda yakin ingin menghapus data siswa berikut?
          </Typography>
          {deleteConfirm.student && (
            <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'grey.100' }}>
              <Typography variant="subtitle2">
                {deleteConfirm.student.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                NIS: {deleteConfirm.student.student_id} • Kelas {deleteConfirm.student.grade}{deleteConfirm.student.classroom && `-${deleteConfirm.student.classroom}`}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Tindakan ini tidak dapat dibatalkan.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={loading}>
            Batal
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Student Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${colors.border}` }}>
          {editingStudent ? 'Edit Siswa' : 'Tambah Siswa'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="NIS (Nomor Induk Siswa)"
                  value={formData.studentId}
                  onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nama Lengkap Siswa"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Pilih Kelas</InputLabel>
                  <Select
                    value={dbClasses.find(c => c.name === (formData.grade && formData.classroom ? `Kelas ${formData.grade}${formData.classroom}` : formData.grade ? `Kelas ${formData.grade}` : ''))?.name || ''}
                    label="Pilih Kelas"
                    onChange={(e) => {
                      const selectedClass = dbClasses.find(c => c.name === e.target.value);
                      if (selectedClass) {
                        const name = selectedClass.name;
                        // Try to find a number 1-6 for grade
                        const digitMatch = name.match(/[1-6]/);
                        // Try to find a letter for classroom (rombel)
                        const letterMatch = name.match(/(?:Kelas\s*\d+|[1-6])\s*([A-Z])/i) || name.match(/[A-Z]$/i);
                        
                        setFormData({
                          ...formData,
                          grade: digitMatch ? parseInt(digitMatch[0]) : '',
                          classroom: letterMatch ? letterMatch[1] || letterMatch[0].toUpperCase() : ''
                        });
                      }
                    }}
                  >
                    {dbClasses.length > 0 ? (
                      dbClasses.map((cls) => (
                        <MenuItem key={cls.id} value={cls.name}>{cls.name}</MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>Belum ada data kelas di database</MenuItem>
                    )}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Kelas terpilih: {formData.grade ? `Kelas ${formData.grade}` : ''} {formData.classroom ? `Rombel ${formData.classroom}` : ''}
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Manual Grade (Kelas)</InputLabel>
                  <Select
                    value={formData.grade ?? ''}
                    label="Manual Grade (Kelas)"
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  >
                    {grades.map((grade) => (
                      <MenuItem key={grade} value={grade}>Kelas {grade}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Manual Rombel</InputLabel>
                  <Select
                    value={formData.classroom ?? ''}
                    label="Manual Rombel"
                    onChange={(e) => setFormData({ ...formData, classroom: e.target.value })}
                  >
                    {classrooms.map((cls) => (
                      <MenuItem key={cls} value={cls}>Rombel {cls}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nomor Telepon Siswa (Opsional)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nomor Telepon Orang Tua"
                  value={formData.parentPhone}
                  onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Jenis Kelamin</InputLabel>
                  <Select
                    value={formData.gender ?? ''}
                    label="Jenis Kelamin"
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  >
                    <MenuItem value="male">Laki-laki</MenuItem>
                    <MenuItem value="female">Perempuan</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Alamat Rumah"
                  multiline
                  rows={3}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Batal
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {editingStudent ? 'Update' : 'Simpan'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Students;
